import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, FlaskConical } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { loadRazorpayScript, getRazorpayConfig } from "@/lib/razorpay";
import { Button } from "@/components/ui/button";

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

/**
 * Deposit payment via Razorpay Checkout — used by both planner (40%) and joinee (25%).
 *
 * Flow (ALWAYS): click -> POST /razorpay/order -> rzp.open() popup with key_id from
 * /api/config/razorpay (never the secret) -> ONLY the popup success handler calls
 * POST /razorpay/verify -> ONLY a verify 200 updates the UI to Live.
 * Dismiss/failure keeps the plan pending_deposit with the CTA available.
 *
 * There is NO auto-complete path on this button. When the backend reports mock mode
 * (possible ONLY with placeholder keys — hard-locked server-side), a SEPARATE,
 * explicitly labelled "Simulate payment (test mode)" control is rendered for testing.
 */
export const RazorpayButton = ({ plan, amount, role, onSuccess, label }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [mockMode, setMockMode] = useState(false);

  useEffect(() => {
    getRazorpayConfig()
      .then((cfg) => setMockMode(!!cfg.mock))
      .catch((err) => console.warn("Could not load Razorpay config", err));
  }, []);

  const verifyPayment = async (res) => {
    try {
      const { data } = await api.post(`/plans/${plan.plan_id}/razorpay/verify`, {
        razorpay_order_id: res.razorpay_order_id,
        razorpay_payment_id: res.razorpay_payment_id,
        razorpay_signature: res.razorpay_signature,
      });
      toast.success(
        data.plan_status === "open"
          ? "Deposit received — your table is LIVE"
          : "Deposit received & verified"
      );
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment verification failed");
    } finally {
      setBusy(false);
    }
  };

  const startPayment = async () => {
    setBusy(true);
    try {
      const cfg = await getRazorpayConfig();
      const { data: order } = await api.post(`/plans/${plan.plan_id}/razorpay/order`);
      await loadRazorpayScript();
      const rzp = new window.Razorpay({
        key: cfg.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "THE CIRCLE",
        description: `${role === "joinee" ? "Joinee" : "Planner"} deposit — ${plan.title}`,
        order_id: order.order_id,
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
          contact: (user?.whatsapp || "").replace(/\D/g, ""),
        },
        theme: { color: "#D4AF37", backdrop_color: "#0B0B0F" },
        modal: {
          ondismiss: () => {
            toast.info("Payment cancelled — your table stays reserved");
            setBusy(false);
          },
        },
        handler: verifyPayment,
      });
      rzp.on("payment.failed", (resp) => {
        console.warn("Razorpay payment failed", resp?.error);
        toast.error(resp?.error?.description || "Payment failed — please try again");
        setBusy(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start payment");
      setBusy(false);
    }
  };

  // TEST MODE ONLY: explicit, user-initiated simulation. Never triggered by plan
  // creation or the main pay CTA. Backend rejects mock-pay (404) unless mock mode
  // is active (which requires placeholder keys).
  const simulatePayment = async () => {
    setBusy(true);
    try {
      await api.post(`/plans/${plan.plan_id}/razorpay/order`);
      const { data: sim } = await api.post(`/plans/${plan.plan_id}/razorpay/mock-pay`);
      await verifyPayment(sim);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Simulation failed");
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <Button
        onClick={startPayment}
        disabled={busy}
        data-testid="razorpay-pay-button"
        className="w-full bg-[#D4AF37] text-[#0B0B0F] hover:bg-[#F1D570] font-semibold rounded-md transition-colors"
      >
        <ShieldCheck className="w-4 h-4 mr-2" strokeWidth={2} />
        {busy ? "Processing..." : label || `Pay 40% Deposit · ${inr(amount)}`}
      </Button>
      {mockMode && (
        <Button
          variant="outline"
          onClick={simulatePayment}
          disabled={busy}
          data-testid="razorpay-simulate-button"
          className="w-full border-[#FFB300]/50 text-[#FFB300] hover:bg-[#FFB300]/10 hover:text-[#FFB300] bg-transparent rounded-md transition-colors"
        >
          <FlaskConical className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Simulate payment (test mode)
        </Button>
      )}
    </div>
  );
};
